import React, { useState, useRef } from 'react';
import { Form, InputNumber, Popover } from 'antd';
import cn from 'classnames';
import { useSearchParams } from 'react-router-dom';
import { isHasAccess } from 'features/validateAccess';
import { TimesheetStatus, useUpdateTimesheetActualHoursMutation, EditableHoursField } from 'entities/timesheet';

import { getTimesheetsEditPermission } from '../../Timesheets.permissions';
import { WarningPopoverContent } from '../WarningPopoverContent';

import styles from './TimesheetActualHoursCell.module.scss';

const TimesheetActualHoursCell: React.FC<{
  recordKey: string;
  highlight?: boolean;
  columnValue: number;
  columnName: EditableHoursField;
  unChangedColumnName: EditableHoursField;
  unChangedColumnValue: number;
  status?: TimesheetStatus;
  version: number;
}> = ({
  recordKey,
  highlight = false,
  columnValue,
  columnName,
  status,
  unChangedColumnName,
  version,
  unChangedColumnValue,
}) => {
  const [form] = Form.useForm();
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('accountId') ?? '';
  const projectId = searchParams.get('projectId') ?? '';

  const canEditTimesheet = isHasAccess(getTimesheetsEditPermission(accountId, projectId));

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isEditingConfirmed, setIsEditingConfirmed] = useState(false);
  const initialValues = { [recordKey]: columnValue };
  const { mutate: updateAssignmentMutation } = useUpdateTimesheetActualHoursMutation(recordKey);

  const isApproved = status === 'APPROVED';

  const onFinish = (values: { [key: string]: number }): void => {
    const updatedHours = values[recordKey];
    updateAssignmentMutation({
      id: recordKey,
      [columnName]: updatedHours,
      [unChangedColumnName]: unChangedColumnValue,
      version,
    });
    setIsEditingConfirmed(false);
  };

  const handleConfirmEdit = (): void => {
    setConfirmationOpen(false);
    setIsEditingConfirmed(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    if (isApproved && !isEditingConfirmed) {
      e.target.blur();
      setConfirmationOpen(true);
    }
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>): void => {
    if (isApproved && !isEditingConfirmed) {
      e.preventDefault();
      setConfirmationOpen(true);
    }
  };

  return !canEditTimesheet ? (
    <span className={styles.statusLabel}>{columnValue}</span>
  ) : (
    <Form initialValues={initialValues} form={form} onFinish={onFinish}>
      <Popover
        open={confirmationOpen}
        placement="bottom"
        arrow={{ pointAtCenter: true }}
        onOpenChange={(newOpen) => {
          if (!newOpen) {
            setConfirmationOpen(false);
          }
        }}
        content={
          <WarningPopoverContent handleOk={handleConfirmEdit} title="approvedTimesheetWarningPopoverContent.text" />
        }
        trigger={[]}
      >
        <div>
          <Form.Item name={recordKey} className={styles.formItem}>
            <InputNumber
              ref={inputRef}
              className={cn(styles.input, { [styles.highlighted]: highlight })}
              max={200}
              min={0}
              readOnly={isApproved && !isEditingConfirmed}
              onFocus={handleInputFocus}
              onClick={handleInputClick}
              onBlur={() => {
                if (columnValue !== form.getFieldValue(recordKey)) {
                  form.submit();
                }
                if (isApproved) {
                  setIsEditingConfirmed(false);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  form.resetFields();
                  if (isApproved) {
                    setIsEditingConfirmed(false);
                  }
                }
              }}
            />
          </Form.Item>
        </div>
      </Popover>
    </Form>
  );
};

export { TimesheetActualHoursCell };
